import { Link } from '@tanstack/react-router';
import { FileText, Files, ListChecks } from 'lucide-react';
import type { ReactNode } from 'react';

import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '@/components/ui/tooltip';

import { cn } from '@/lib/utils';

import { plainStatusText } from './layerSteps';

export type LayerStep = 'file' | 'documents' | 'fields';

/** Section anchors on the file page that steps 1 and 2 jump to. */
export const FILE_SECTION_ID = 'gt-file';
export const DOCUMENTS_SECTION_ID = 'gt-docs';
/** Tells an editor with collapsible sections to open the one with this id. */
export const OPEN_SECTION_EVENT = 'gt-open-section';

const STEP_ICON = {
    file: FileText,
    documents: Files,
    fields: ListChecks,
} as const;

const CHIP_COLOR: Record<LayerStep, string> = {
    file: 'bg-[#476969]',
    documents: 'bg-[#2F7F7F]',
    fields: 'bg-[#7C3AED]',
};

/** The icon circle that marks a layer wherever it is named. */
export function LayerChip({
    step,
    className,
}: {
    step: LayerStep;
    className?: string;
}) {
    const Icon = STEP_ICON[step];
    return (
        <span
            aria-hidden="true"
            className={cn(
                'inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-white',
                CHIP_COLOR[step],
                className,
            )}
            data-testid={`LayerChip__${step}`}
        >
            <Icon className="h-[60%] w-[60%]" strokeWidth={2.5} data-testid={`LayerChip__${step}Icon`} />
        </span>
    );
}

const STEP_TITLE: Record<LayerStep, string> = {
    file: 'File',
    documents: 'Documents',
    fields: 'Fields',
};

const STEP_HEADING: Record<LayerStep, string> = {
    file: 'Step 1 · File',
    documents: 'Step 2 · Documents',
    fields: 'Step 3 · Fields',
};

const STEP_HELP: Record<LayerStep, string> = {
    file: 'The whole PDF as it was uploaded. Check that every page belongs to a document, and add a file remark if something is wrong with the file itself (missing pages, unreadable scan).',
    documents:
        'Split the file into separate documents, for example one work card that runs from page 3 to 4, and choose a type for each. Press Create documents to save the split.',
    fields: 'Fill in the answers for each document, such as dates or part numbers. This opens once the documents are created and their pages have not changed since.',
};

function StepTip({
    step,
    status,
    children,
}: {
    step: LayerStep;
    status: string;
    children: ReactNode;
}) {
    return (
        <Tooltip>
            <TooltipTrigger asChild>{children}</TooltipTrigger>
            <TooltipContent
                className="max-w-[280px] space-y-1 text-xs"
                data-testid={`GroundTruthLayerSteps__${step}Tip`}
            >
                <p className="font-semibold">{STEP_HEADING[step]}</p>
                <p>{STEP_HELP[step]}</p>
                <p>
                    <span className="font-semibold">Now:</span>{' '}
                    {plainStatusText(status)}
                </p>
            </TooltipContent>
        </Tooltip>
    );
}

const stepCls =
    'inline-flex h-7 items-center gap-2 whitespace-nowrap rounded-full pl-2 pr-3 text-xs no-underline';
const idleCls = 'border border-[#E2F0F0] bg-white';
const currentCls = (step: LayerStep) =>
    step === 'fields'
        ? 'border-[1.5px] border-violet-600 bg-violet-50'
        : 'border-[1.5px] border-[#32ACAC] bg-[#EFF9F9]';

function StepBody({ step, status }: { step: LayerStep; status: string }) {
    return (
        <>
            <LayerChip step={step} className="h-4 w-4" />
            <strong className="font-semibold text-[#0F2A2A]">
                {STEP_TITLE[step]}
            </strong>
            <span
                className="hidden text-[#476969] @[1100px]/gt:inline"
                data-testid={`GroundTruthLayerSteps__${step}Status`}
            >
                {status}
            </span>
        </>
    );
}

function scrollToSection(id: string) {
    window.dispatchEvent(new CustomEvent(OPEN_SECTION_EVENT, { detail: id }));
    requestAnimationFrame(() =>
        document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' }),
    );
}

interface GroundTruthLayerStepsProps {
    ingestedFileId: number;
    /** Short status beside each step's name; hidden in a narrow header and kept in the tooltip. */
    status: Record<LayerStep, string>;
    /** The step or steps the page works on; they get the highlighted border. */
    current: LayerStep[];
    /** On the file page steps 1 and 2 scroll to their section; elsewhere they open the file page there. */
    onFilePage: boolean;
    /** The document step 3 opens, when the page is not already on its fields. */
    fieldsDocId?: number | null;
}

/** The File, Documents, Fields chips for a page header; the header must be a `@container/gt`. */
export function GroundTruthLayerSteps({
    ingestedFileId,
    status,
    current,
    onFilePage,
    fieldsDocId = null,
}: GroundTruthLayerStepsProps) {
    const classes = (step: LayerStep) =>
        cn(stepCls, current.includes(step) ? currentCls(step) : idleCls);
    const aria = (step: LayerStep) =>
        current.includes(step) ? ('step' as const) : undefined;

    const fileStep = (step: 'file' | 'documents') => {
        const anchor =
            step === 'file' ? FILE_SECTION_ID : DOCUMENTS_SECTION_ID;
        const body = <StepBody step={step} status={status[step]} />;
        if (onFilePage) {
            return (
                <StepTip step={step} status={status[step]}>
                <button
                    type="button"
                    className={classes(step)}
                    aria-current={aria(step)}
                    onClick={() => scrollToSection(anchor)}
                    data-testid={`GroundTruthLayerSteps__${step}`}
                >
                    {body}
                </button>
                </StepTip>
            );
        }
        return (
            <StepTip step={step} status={status[step]}>
            <Link
                to="/ground-truth/$ingestedFileId"
                params={{ ingestedFileId }}
                hash={anchor}
                className={classes(step)}
                data-testid={`GroundTruthLayerSteps__${step}`}
            >
                {body}
            </Link>
            </StepTip>
        );
    };

    const fieldsBody = <StepBody step="fields" status={status.fields} />;

    return (
        <TooltipProvider delayDuration={200}>
        <nav
            aria-label="Ground truth layers"
            className="flex flex-none items-center gap-2"
            data-testid="GroundTruthLayerSteps"
        >
            {fileStep('file')}
            <span aria-hidden="true" className="text-xs text-[#94B8B8]">
                →
            </span>
            {fileStep('documents')}
            <span aria-hidden="true" className="text-xs text-[#94B8B8]">
                →
            </span>
            <StepTip step="fields" status={status.fields}>
                {fieldsDocId !== null && !current.includes('fields') ? (
                    <Link
                        to="/ground-truth/$ingestedFileId/$docId"
                        params={{ ingestedFileId, docId: fieldsDocId }}
                        className={classes('fields')}
                        data-testid="GroundTruthLayerSteps__fields"
                    >
                        {fieldsBody}
                    </Link>
                ) : (
                    <span
                        tabIndex={0}
                        className={classes('fields')}
                        aria-current={aria('fields')}
                        data-testid="GroundTruthLayerSteps__fields"
                    >
                        {fieldsBody}
                    </span>
                )}
            </StepTip>
        </nav>
        </TooltipProvider>
    );
}
